import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import {
  NgForm,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  FormControl,
  AbstractControl,
  Validators,
  ValidationErrors
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { TdLoadingService, TdDialogService } from '@covalent/core';
import { Subscription } from 'rxjs/Subscription';

import { AuthService } from '../../../../services/auth.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  /**
   * The email password form group
   */
  phonePasswordForm: FormGroup;

  /**
   * The update password form group
   */
  updatePasswordForm: FormGroup;

  isTokenPresent = false;

  /**
   * Subscriptions
   */
  private _subs: Set<Subscription> = new Set();

  /**
   * Error keyword/message map
   */
  static readonly errorMap = {
    invalidEmail: 'შეყვანილი ელ-ფოსტა არ ფიქსირდება სისტემაში'
  };

  /**
   * Error form and control map
   */
  static readonly errorFormControlMap = {
    invalidEmail: {
      formName: 'phonePasswordForm',
      controlName: 'username'
    },

    INVALID_TOKEN: {
      formName: 'updatePasswordForm',
      controlName: 'token'
    }
  };

  /**
   * Constructor
   *
   * @param _fb
   * @param _router
   * @param _activatedRoute
   * @param _httpClient
   * @param _viewContainerRef
   * @param _loadingService
   * @param _dialogService
   * @param _authService
   */
  constructor(
    private _fb: FormBuilder,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _httpClient: HttpClient,
    private _viewContainerRef: ViewContainerRef,
    private _loadingService: TdLoadingService,
    private _dialogService: TdDialogService,
    private _authService: AuthService
  ) {
    this.phonePasswordForm = _fb.group({
      username: ['', Validators.compose([Validators.required])]
    });

    this.updatePasswordForm = _fb.group({
      token: ['', Validators.required],
      password: [
        '',
        Validators.compose([Validators.required, Validators.minLength(6)])
      ],
      passwordConfirmation: [
        '',
        Validators.compose([Validators.required, this.samePassword])
      ]
    });
  }

  /**
   * Init
   */
  ngOnInit() {
    /* this._subs.add(
      this._activatedRoute.params.subscribe((params) => {
        this.updatePasswordForm.controls['token'].setValue(params.token);
      })
    );*/

    Array.prototype.forEach.call(
      ['phonePasswordForm', 'updatePasswordForm'],
      (formName: string) => {
        this._subs.add(
          this[formName].valueChanges.subscribe((value) => {
            // this[formName].markAsPristine();
            // this[formName].markAsUntouched();
            Object.keys(ResetPasswordComponent.errorFormControlMap)
              .filter(
                (key) =>
                  ResetPasswordComponent.errorFormControlMap[key].formName ===
                  formName
              )
              .forEach((key) => {
                let {
                  controlName
                } = ResetPasswordComponent.errorFormControlMap[key];
                let control = this[formName].controls[controlName];
                if (control.hasError(key)) {
                  // control.setErrors({ [key]: null });
                  // control.markAsPristine();
                  // control.markAsUntouched();
                  delete control.errors[key];
                  if (Object.keys(control.errors).length === 0) {
                    control.setErrors(null);
                  }
                }
              });
          })
        );
      }
    );
  }

  /**
   * Destroy
   */
  ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Token
   */
  /*get token() {
    return this.updatePasswordForm.controls['token'].value;
  }*/

  /**
   * Returns page title
   */
  get pageTitle() {
    return this.updatePasswordForm.controls['token'].value
      ? 'პაროლის განახლება'
      : 'პაროლის აღდგენა';
  }

  /**
   * Returns errorMap
   */
  get errorMap() {
    return ResetPasswordComponent.errorMap;
  }

  /**
   * Opens alert dialog
   *
   * @param message
   * @param title
   */
  private _openAlert(message: string, title: string = '') {
    return this._dialogService.openAlert({
      message: message,
      viewContainerRef: this._viewContainerRef, //OPTIONAL
      title: title, //OPTIONAL, hides if not provided
      closeButton: 'დახურვა', //OPTIONAL, defaults to 'CLOSE'
      width: '400px' //OPTIONAL, defaults to 400px
    });
  }

  /**
   * Sets form errors
   *
   * @param errors
   */
  private _handleFormErrors(errors: any[]) {
    errors.forEach((err) => {
      if (ResetPasswordComponent.errorFormControlMap[err.keyword]) {
        // Extract formName and controlName
        const {
          formName,
          controlName
        } = ResetPasswordComponent.errorFormControlMap[err.keyword];

        // Fetch the form
        const form: FormGroup = this[formName];

        // Check for form and control existance
        if (form && form.contains(controlName)) {
          // Set form control errors
          form.controls[controlName].setErrors({ [err.keyword]: true });
        }
      }
    });
  }

  /**
   * Password confirmation validator
   *
   * @param control
   */
  samePassword(control: AbstractControl): ValidationErrors | null {
    if (!control || !control.parent) {
      return null;
    }

    if (control.value !== control.parent.get('password').value) {
      return { passwordMismatch: true };
    }

    return null;
  }

  /**
   * Sends password update link to given email
   *
   * @param event
   */
  emailPassword(event: Event) {
    if (this.phonePasswordForm.valid) {
      const data = Object.assign(
        { link: `${location.protocol}//${location.host}/auth/reset-password/` },
        this.phonePasswordForm.value
      );

      this._loadingService.register();
      this._httpClient
        .post('/api/auth/password/email', { data })
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe(
          (res) => {
            if (res['errors']) {
              this._handleFormErrors(res['errors']);
            } else if (res['success']) {
              this._openAlert(
                'პაროლის აღსადგენად თქვენს ელექტრონულ ფოსტაზე იხილეთ შესაბამისი ბმული.'
              )
                .afterClosed()
                .subscribe(() => {
                  const isAdmin = this._router.url.includes('admin');
                  const urlEndPoint = isAdmin ? 'private' : 'public';

                  document.location.href = `${this.ssoUrl}/${urlEndPoint}/auth/login?redirectUrl=${document.location.href}`;
                  //   this._router.navigate(['/auth']);
                });
            }
          },
          (error) => {
            this._openAlert(error.statusText);
          }
        );
    }
  }

  phonePassword() {
    if (this.phonePasswordForm.valid) {
      this._loadingService.register();
      this._httpClient
        .post(
          `/api/um/user/${this.phonePasswordForm.value.username}/forgot-password/by-phone`,
          {}
        )
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe(
          (res: any) => {
            if (res.result.errors) {
              this._handleFormErrors(res.result.errors);
            } else if (res.result.data) {
              this.isTokenPresent = true;
            }
          },
          (error) => {
            this._openAlert(error.statusText);
          }
        );
    }
  }

  /**
   * Updates password
   */
  updatePassword() {
    if (this.updatePasswordForm.valid) {
      const data = Object.assign({}, this.updatePasswordForm.value);
      this._loadingService.register();
      this._httpClient
        .put(
          `/api/um/user/token/${this.updatePasswordForm.value.token}/password`,
          { data }
        )
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe(
          (res: any) => {
            if (res.result.errors) {
              this._handleFormErrors(res.result.errors);
            } else {
              this._openAlert('პაროლი წარმატებით განახლდა.')
                .afterClosed()
                .subscribe(() => {
                  const isAdmin = this._router.url.includes('admin');
                  const urlEndPoint = isAdmin ? 'private' : 'public';

                  document.location.href = `${this.ssoUrl}/${urlEndPoint}/auth/login?redirectUrl=${document.location.href}`;
                  //   this._router.navigate(['/auth']);
                });
            }
          },
          (error) => {
            this._openAlert(error.statusText);
          }
        );
    }
  }
  navigateAuth() {
    const isAdmin = this._router.url.includes('admin');
    const urlEndPoint = isAdmin ? 'private' : 'public';

    document.location.href = `${this.ssoUrl}/${urlEndPoint}/auth/login?redirectUrl=${document.location.href}`;
  }
  get ssoUrl(): string {
    return environment.ssoUrl;
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/components/auth/components/reset-password/reset-password.component.ts